import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

// --- Configuration ---
const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

interface VoucherGroup {
    From: number;
    To: number;
    Used: number;
    Available: number;
    Details: {
        VoucherCode: string;
        SchoolCode: string;
        DistributorName: string;
        ExpiryDate: string;
        Status: string;
        VoucherUsedFor: string;
        SendBrickReportToFamily: string;
        SendKnowledgeGapReportToFamily: string;
        SendLessonPlanReportToFamily: string;
        Remarks: string;
        DistEMail: string;
    }
}

interface DistinctRow {
    VoucherCode: string;
    SchoolCode: string;
    Login: string;
    ExpiryDate: string;
    Status: string;
    Remarks: string;
}

const MD_ManageVoucherCodes: React.FC = () => {
    // --- State ---
    const [distributors, setDistributors] = useState<{ Name: string, SchoolCode: string }[]>([]);
    const [gridData, setGridData] = useState<VoucherGroup[]>([]);

    // Data for sub-screens
    const [distinctData, setDistinctData] = useState<DistinctRow[]>([]);
    const [usageData, setUsageData] = useState<any[]>([]);

    // Views: 'Main', 'Distinct', 'Usage'
    const [viewMode, setViewMode] = useState<'Main' | 'Distinct' | 'Usage'>('Main');
    const [viewTitle, setViewTitle] = useState(''); // For sub-headers

    const [selectedIdx, setSelectedIdx] = useState<number>(-1);
    const [nextNo, setNextNo] = useState('');
    const [loading, setLoading] = useState(false);

    // Form State
    const [mode, setMode] = useState<'A' | 'R'>('A');
    const [txtStart, setTxtStart] = useState('');
    const [txtEnd, setTxtEnd] = useState('');
    const [selDistributor, setSelDistributor] = useState('');
    const [txtUsedFor, setTxtUsedFor] = useState('');
    const [txtExpiry, setTxtExpiry] = useState('');
    const [status, setStatus] = useState<'A' | ''>('');
    const [chkBrick, setChkBrick] = useState(false);
    const [chkGap, setChkGap] = useState(false);
    const [chkWeekly, setChkWeekly] = useState(false);
    const [txtRemarks, setTxtRemarks] = useState('');
    const [txtEmail, setTxtEmail] = useState('');
    const [saving, setSaving] = useState(false);

    // --- Styles ---
    const styles = {
        container: { fontFamily: 'Arial', fontSize: '11px', backgroundColor: '#fff', padding: '10px', height: '97vh', display: 'flex', flexDirection: 'column' as const },

        // Header Bars
        headerBar: {
            backgroundColor: '#c7ddfd', // Light blue from screenshot
            color: '#2859ac',           // Dark blue font
            fontWeight: 'bold',
            textAlign: 'center' as const,
            padding: '8px',
            border: '1px solid #80a0ff',
            borderBottom: 'none',
            fontSize: '12px'
        },

        // Grid Container
        gridContainer: {
            flex: 1, // Fill available space
            overflow: 'auto',
            border: '1px solid #80a0ff',
            marginBottom: '10px',
            backgroundColor: 'white',
            position: 'relative' as const
        },

        // Table Styling
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontSize: '10pt', // Requested Font Size 
            borderBottom: "1px solid #80a0ff"
        },

        th: {
            backgroundColor: '#c7ddfd',
            color: '#2859ac',
            fontWeight: 'normal',
            padding: '8px 4px', // More padding
            border: '1px solid #80a0ff',
            position: 'sticky' as const,
            top: 0,
            whiteSpace: 'nowrap' as const,
            textAlign: 'left' as const,
            zIndex: 10
        },

        td: {
            padding: '8px 4px', // Requested Padding
            borderRight: '1px solid #80a0ff',
            color: 'inherit',
            whiteSpace: 'nowrap' as const
        },

        tdCenter: { textAlign: 'center' as const, padding: '8px 4px', borderLeft: '1px solid #80a0ff', borderRight:'1px solid #80a0ff', color: 'inherit' },

        // Row Colors
        rowEven: { backgroundColor: '#ffffff', color: '#000' },
        rowOdd: { backgroundColor: '#f4faff', color: '#000' },
        rowSelected: { backgroundColor: '#ffa500', color: '#fff' }, // Orange Selection

        // Form Area
        formArea: {
            border: '1px solid #ccc',
            padding: '5px',
            fontSize: '11pt'
        },
        formLink: { color: 'blue', textDecoration: 'underline', fontWeight: 'extrabold', cursor: 'pointer', marginBottom: '10px', display: 'block' },
        formTable: { width: '100%' },
        formLabel: { padding: '0px' },
        formInput: { padding: '0px' },
        txt: { border: '1px solid #aaa', width: '200px', fontSize: '11pt', padding: '2px' },
        ddl: { width: '300px', fontSize: '11pt', padding: '2px' },

        // Buttons
        btn: {
            cursor: 'pointer',
            backgroundColor: '#eee',
            border: '1px solid #999',
            padding: '5px 15px',
            marginRight: '15px',
            fontSize: '11pt',
            borderRadius: '2px',
            minWidth: '80px'
        },
        btnDisabled: {
            cursor: 'default',
            backgroundColor: '#f0f0f0',
            border: '1px solid #ccc',
            color: '#aaa',
            padding: '5px 15px',
            marginRight: '15px',
            fontSize: '11pt',
            minWidth: '80px'
        },

        // Bottom Bar (Distinct/Usage Page Footer)
        bottomBar: {
            backgroundColor: '#c7ddfd',
            border: '1px solid #80a0ff',
            borderTop: 'none',
            padding: '5px',
            textAlign: 'center' as const,
            fontSize: '11px'
        }
    };

    // --- Load ---
    useEffect(() => {
        document.body.style.backgroundColor = "#fff"
        fetchInit();
        fetchGrid();
    }, []);

    const fetchInit = async () => {
        const res = await fetch(`${API_BASE}api/voucher-init/`);
        const data = await res.json();
        setDistributors(data.distributors);
        setNextNo(data.nextNo);
        setTxtStart(data.nextNo);
    };

    const fetchGrid = async () => {
        setLoading(true);
        const res = await fetch(`${API_BASE}api/voucher-list/`);
        const data = await res.json();
        setGridData(data.data);
        setLoading(false);
        resetForm();
    };

    const resetForm = () => {
        setMode('A');
        // setTxtStart(nextNo);
        setTxtEnd('');
        setSelDistributor('');
        setTxtUsedFor('');
        setTxtExpiry('');

        // Original VB starts as Inactive
        setStatus('');

        setChkBrick(false);
        setChkGap(false);
        setChkWeekly(false);

        setTxtRemarks('');
        setTxtEmail('');
        setSelectedIdx(-1);
        setViewMode('Main');
    };
    // --- Handlers ---

    const handleRowClick = (idx: number) => {
        setSelectedIdx(idx);
        const row = gridData[idx];
        const d = row.Details;

        setMode('R');
        setTxtStart(row.From.toString());
        setTxtEnd(row.To.toString());
        setSelDistributor(d.SchoolCode);
        setTxtUsedFor(d.VoucherUsedFor || '');
        setTxtExpiry(d.ExpiryDate ? new Date(d.ExpiryDate).toLocaleDateString('en-GB') : '');
        setStatus(d.Status === 'A' ? 'A' : '');
        setChkBrick(d.SendBrickReportToFamily === 'Y');
        setChkGap(d.SendKnowledgeGapReportToFamily === 'Y');
        setChkWeekly(d.SendLessonPlanReportToFamily === 'Y');
        setTxtRemarks(d.Remarks || '');
        setTxtEmail(d.DistEMail || '');
    };

    const handleSave = async () => {
        if (saving) return;

        if (!validateForm()) {
            return;
        }

        const payload = {
            ARD: mode,
            startNo: txtStart,
            endNo: txtEnd,
            distributor: selDistributor,
            expiry: txtExpiry,
            status: status,
            remarks: txtRemarks,
            usedFor: txtUsedFor,
            email: txtEmail,
            brick: chkBrick,
            gap: chkGap,
            plan: chkWeekly
        };

        setSaving(true);

        try {
            const res = await fetch(`${API_BASE}api/voucher-save/`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.status === "OK") {
                alert("Saved Successfully");

                await fetchGrid();

                if (mode === "A") {
                    await fetchInit();
                }
            } else {
                alert(data.error || "Save Failed");
            }
        } catch (err) {
            console.error(err);
            alert("Save Failed");
        } finally {
            setSaving(false);
        }
    };

    const isValidDate = (dateStr: string) => {
        const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        if (!regex.test(dateStr)) return false;

        const [, dd, mm, yyyy] = dateStr.match(regex)!;

        const date = new Date(
            Number(yyyy),
            Number(mm) - 1,
            Number(dd)
        );

        return (
            date.getDate() === Number(dd) &&
            date.getMonth() === Number(mm) - 1 &&
            date.getFullYear() === Number(yyyy)
        );
    };


    const validateForm = () => {

        if (!selDistributor) {
            alert("Select a Dealer / Distributor.");
            return false;
        }

        if (!txtStart.trim()) {
            alert("Enter Start Number");
            return false;
        }

        if (!txtEnd.trim()) {
            alert("Enter End Number");
            return false;
        }

        if (parseInt(txtEnd, 10) < parseInt(txtStart, 10)) {
            alert("'End Number' cannot be less than 'Start Number'");
            return false;
        }

        if (
            mode === "A" &&
            parseInt(txtStart, 10) < parseInt(nextNo, 10)
        ) {
            alert(
                "Entered range of voucher codes already exists either partially or fully."
            );
            return false;
        }

        if (!txtExpiry.trim()) {
            alert("Enter an Expiry Date.");
            return false;
        }

        if (!isValidDate(txtExpiry)) {
            alert("Please enter a valid date in the format dd/mm/yyyy.");
            return false;
        }

        if (txtEmail.trim() && !isValidEmail(txtEmail)) {
            alert("Enter valid eMail");
            return false;
        }

        if (
            mode === "R" &&
            selectedIdx !== -1 &&
            gridData[selectedIdx]?.Used > 0
        ) {
            alert(
                "Please note that changes will be done only for UNUSED voucher(s).\n" +
                "Used voucher(s) will not be modified.\n\n" +
                "Click OK to continue."
            );
        }

        return true;
    };


    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleDistinct = async () => {
        if (selectedIdx === -1) {
            alert("Please select a row.");
            return;
        }
        const row = gridData[selectedIdx];

        setLoading(true);
        setViewTitle(`Distinct voucher code(s) ${row.From} - ${row.To}`);

        const res = await fetch(`${API_BASE}api/voucher-distinct/?start=${row.From}&end=${row.To}`);
        const data = await res.json();
        setDistinctData(data.data);
        setViewMode('Distinct');
        setLoading(false);
    };

    const handleUsage = async () => {
        if (selectedIdx === -1) {
            alert("Please select a row.");
            return;
        }
        const row = gridData[selectedIdx];

        setLoading(true);
        // Construct exact title from screenshot logic
        setViewTitle(`Usage details of voucher code(s): ${row.From} - ${row.To}.<br/>Voucher used for: ${row.Details.VoucherUsedFor || ''}`);

        const res = await fetch(`${API_BASE}api/voucher-usage/?start=${row.From}&end=${row.To}`);
        const data = await res.json();
        setUsageData(data.data);
        setViewMode('Usage');
        setLoading(false);
    };

    // --- RENDER: USAGE DETAILS ---
    if (viewMode === 'Usage') {
        return (
            <div style={styles.container}>
                <div style={styles.headerBar} dangerouslySetInnerHTML={{ __html: viewTitle }} />

                <div style={styles.gridContainer}>
                    {loading ? <Loading /> : (
                        <table style={styles.table}>
                            <thead>
                                {/* Merged Headers */}
                                <tr style={{ width: "100vw" }}>
                                    <th style={{ ...styles.th, borderBottom: 'none' }} colSpan={2}></th>

                                    <th
                                        style={{ ...styles.th, textAlign: 'center' }}
                                        colSpan={5}
                                    >
                                        Family
                                    </th>

                                    <th
                                        style={{ ...styles.th, textAlign: 'center' }}
                                        colSpan={5}
                                    >
                                        Child
                                    </th>

                                    <th
                                        style={{ ...styles.th, borderBottom: 'none' }}
                                        colSpan={2}
                                    ></th>
                                </tr>
                                <tr>
                                    <th style={{ ...styles.th, textAlign: 'center', width: "30px" }}>
                                        #
                                    </th>

                                    <th style={styles.th}>
                                        Voucher<br />Code
                                    </th>

                                    <th style={styles.th}>
                                        School Code
                                    </th>

                                    <th style={styles.th}>
                                        Login
                                    </th>

                                    <th style={styles.th}>
                                        Registered<br />On
                                    </th>

                                    <th style={styles.th}>
                                        First Name
                                    </th>

                                    <th style={styles.th}>
                                        Surname
                                    </th>

                                    <th style={styles.th}>
                                        No.Of<br />Children
                                    </th>

                                    <th style={styles.th}>
                                        Student
                                    </th>

                                    <th style={styles.th}>
                                        Grade/Year
                                    </th>

                                    <th style={styles.th}>
                                        Report.No.
                                    </th>

                                    <th style={styles.th}>
                                        Score
                                    </th>

                                    <th style={styles.th}>
                                        Test Done on
                                    </th>

                                    <th style={{ ...styles.th, width: "500px" }}>
                                        Country
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {usageData.map((d, i) => (
                                    <tr key={i} style={i % 2 !== 0 ? styles.rowOdd : styles.rowEven}>
                                        <td style={styles.tdCenter}>{i + 1}</td>
                                        <td style={styles.td}>{d.VoucherCode}</td>
                                        <td style={styles.td}>{d.SchoolCode}</td>
                                        <td style={styles.td}>{d.Login}</td>
                                        <td style={styles.tdCenter}>{d.RegisteredOn ? new Date(d.RegisteredOn).toLocaleDateString('en-GB') : ''}</td>
                                        <td style={styles.td}>{d.FName}</td>
                                        <td style={styles.td}>{d.LName}</td>
                                        <td style={styles.tdCenter}>{d.ChildrenCount || ''}</td>
                                        <td style={styles.td}>{d.StudentName}</td>
                                        <td style={styles.tdCenter}>{d.StudyYear}</td>
                                        <td style={styles.tdCenter}>{d.ReportNo}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{d.Score}</td>
                                        <td style={styles.td}>{d.TestCompletionDate ? new Date(d.TestCompletionDate).toLocaleDateString('en-GB') : ''}</td>
                                        <td style={styles.td}>{d.Country}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer Buttons for Reports */}
                <div style={{ borderTop: '1px solid #ccc', padding: '10px', textAlign: 'left' }}>
                    <button style={styles.btn} onClick={() => setViewMode('Main')}>Back</button>
                    <button style={styles.btnDisabled} disabled>Brick Report</button>
                    <button style={styles.btnDisabled} disabled>K.Gap Report</button>
                    <button style={styles.btnDisabled} disabled>Nav.Report</button>
                    <button style={styles.btnDisabled} disabled>Stud. Details</button>
                    <span style={{ marginLeft: '20px', padding: "1px", fontSize: "11pt" }}>Mail Reports & details to: </span>
                    <input type="text" style={{ border: '1px solid black', padding: "5px" }} />
                    <button style={styles.btnDisabled} disabled>EMail</button>
                </div>
            </div>
        )
    }

    // --- RENDER: DISTINCT ---
    if (viewMode === 'Distinct') {
        return (
            <div style={styles.container}>
                <div style={styles.headerBar}>{viewTitle}</div>
                <div style={styles.gridContainer}>
                    {loading ? <Loading /> : (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, textAlign: 'center', width: '30px' }}>#</th>
                                    <th style={styles.th}>VoucherCode</th>
                                    <th style={styles.th}>SchoolCode</th>
                                    <th style={styles.th}>Login</th>
                                    <th style={styles.th}>ExpiryDate</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={{ ...styles.th, width: "1500px" }}>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {distinctData.map((d, i) => (
                                    <tr key={i} style={i % 2 !== 0 ? styles.rowOdd : styles.rowEven}>
                                        <td style={styles.tdCenter}>{i + 1}</td>
                                        <td style={styles.td}>{d.VoucherCode}</td>
                                        <td style={styles.td}>{d.SchoolCode}</td>
                                        <td style={styles.td}>{d.Login}</td>
                                        <td style={styles.td}>{d.ExpiryDate ? new Date(d.ExpiryDate).toLocaleDateString('en-GB') : ''}</td>
                                        <td style={styles.td}>{d.Status}</td>
                                        <td style={styles.td}>{d.Remarks}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div style={styles.bottomBar}>
                    <label><input type="checkbox" /> Show Nulls.</label>
                    &nbsp; &lt;&lt; &lt; 1 of 1 &gt; &gt;&gt;
                </div>
                <div style={{ marginTop: '10px', textAlign: 'left' }}>
                    <button style={styles.btn} onClick={() => setViewMode('Main')}>Back</button>
                </div>
            </div>
        );
    }

    // --- RENDER: MAIN ---
    return (
        <div style={styles.container}>
            <div style={styles.headerBar}>List of All Voucher codes</div>

            <div style={styles.gridContainer}>
                {loading ? <Loading /> : (
                    <table style={styles.table}>
                        <thead>
                            {/* Merged Header */}
                            <tr style={{ width: "100vw" }}>
                                <th style={{ ...styles.th, borderBottom: 'none' }} colSpan={3}></th>
                                <th style={{ ...styles.th, textAlign: 'center', border: '1px solid #80a0ff' }} colSpan={2}>No.of vouchers</th>
                                <th style={{ ...styles.th, borderBottom: 'none' }} colSpan={5}></th>
                                <th style={{ ...styles.th, textAlign: 'center', border: '1px solid #80a0ff' }} colSpan={3}>Report to Family</th>
                                <th style={{ ...styles.th, borderBottom: 'none', }} colSpan={3}></th>
                            </tr>
                            <tr>
                                <th style={{ ...styles.th, width: '30px', textAlign: 'center' }}>#</th>
                                <th style={styles.th}>From</th>
                                <th style={styles.th}>To</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Used</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Available</th>
                                <th style={styles.th}>Code</th>
                                <th style={styles.th}>eDealer/Distributor Name</th>
                                <th style={styles.th}>Expiry Date</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Voucher<br />used for</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>Brick</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>KnowledgeGap</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>LessonPlan</th>
                                <th style={styles.th}>Remarks</th>
                                <th style={styles.th}>Dist.EMailID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((row, i) => {
                                const isSelected = selectedIdx === i;
                                const isOdd = (i + 1) % 2 !== 0;
                                let rowStyle = isOdd ? styles.rowOdd : styles.rowEven;
                                let textColor = '#000';

                                if (isSelected) {
                                    rowStyle = styles.rowSelected;
                                    textColor = '#fff';
                                }

                                return (
                                    <tr key={i} onClick={() => handleRowClick(i)} style={{ ...rowStyle, cursor: 'pointer', color: textColor, borderRight: "1px solid blue" }}>
                                        <td style={{ ...styles.tdCenter, color: textColor }}>{i + 1}</td>
                                        <td style={{ ...styles.tdCenter, color: textColor }}>{row.From}</td>
                                        <td style={{ ...styles.tdCenter, color: textColor }}>{row.To}</td>
                                        <td style={{ ...styles.tdCenter, color: isSelected ? 'inherit' : 'red', fontWeight: 'bold' }}>{row.Used > 0 ? row.Used : ''}</td>
                                        <td style={{ ...styles.tdCenter, borderRight: "1px solid #80a0ff", color: isSelected ? 'inherit' : 'green', fontWeight: 'bold' }}>{row.Available > 0 ? row.Available : ''}</td>
                                        <td style={{ ...styles.td, color: textColor }}>{row.Details.SchoolCode}</td>
                                        <td style={{ ...styles.td, color: textColor }}>{row.Details.DistributorName}</td>
                                        <td style={{ ...styles.td, color: textColor }}>{row.Details.ExpiryDate ? new Date(row.Details.ExpiryDate).toLocaleDateString('en-GB') : ''}</td>
                                        <td style={{ ...styles.tdCenter, color: textColor, borderRight: "1px solid #80a0ff" }}>{row.Details.Status}</td>
                                        <td style={{ ...styles.td, color: textColor }}>{row.Details.VoucherUsedFor}</td>
                                        <td style={{ ...styles.tdCenter, color: textColor }}>{row.Details.SendBrickReportToFamily}</td>
                                        <td style={{ ...styles.tdCenter, color: textColor }}>{row.Details.SendKnowledgeGapReportToFamily}</td>
                                        <td style={{ ...styles.tdCenter, color: textColor, borderRight: "1px solid #80a0ff", }}>{row.Details.SendLessonPlanReportToFamily}</td>
                                        <td style={{ ...styles.td, color: textColor }}>{row.Details.Remarks}</td>
                                        <td style={{ ...styles.td, color: textColor }}>{row.Details.DistEMail}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* --- FORM AREA --- */}
            <div style={styles.formArea}>
                <div style={{ ...styles.formLink, fontWeight: "bold" }} onClick={resetForm}>
                    {mode === 'A' ? "Add Voucher code(s)" : "Edit Voucher code(s)"}
                </div>
                <table style={styles.formTable}>
                    <tbody>
                        <tr>
                            <td style={styles.formLabel}>Start Number:</td>
                            <td style={styles.formInput}>
                                <input
                                    type="text"
                                    style={styles.txt}
                                    value={txtStart}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (!/^\d*$/.test(value)) {
                                            alert("Enter numerals only in this field.");
                                            return;
                                        }

                                        setTxtStart(value);
                                    }}
                                    disabled={mode === 'A'}
                                />
                            </td>
                            <td style={styles.formLabel}>Allocate the above vouchers To:</td>
                            <td style={styles.formInput}>
                                <select style={styles.ddl} value={selDistributor} onChange={e => setSelDistributor(e.target.value)}>
                                    <option value="">-- Select Distributor --</option>
                                    {distributors.map(d => <option key={d.SchoolCode} value={d.SchoolCode}>{d.Name}</option>)}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.formLabel}>End Number:</td>
                            <td style={styles.formInput}>
                                <input
                                    type="text"
                                    style={styles.txt}
                                    value={txtEnd}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (!/^\d*$/.test(value)) {
                                            alert("Enter numerals only in this field.");
                                            return;
                                        }

                                        setTxtEnd(value);
                                    }}
                                />
                            </td>
                            <td style={styles.formLabel}>Voucher codes to be used for:</td>
                            <td style={styles.formInput}>
                                <input type="text" style={{ ...styles.txt, width: '90%' }} value={txtUsedFor} onChange={e => setTxtUsedFor(e.target.value)} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.formLabel}>Expiry Date (dd/mm/yyyy):</td>
                            <td style={styles.formInput}>
                                <input type="text" style={styles.txt} value={txtExpiry} onChange={e => setTxtExpiry(e.target.value)} />
                            </td>
                            <td style={styles.formLabel}>Send reports to family:</td>
                            <td style={styles.formInput}>
                                <label><input type="checkbox" checked={chkBrick} onChange={e => setChkBrick(e.target.checked)} /> Brick</label>&nbsp;&nbsp;
                                <label><input type="checkbox" checked={chkGap} onChange={e => setChkGap(e.target.checked)} /> Knowledge gap</label>&nbsp;&nbsp;
                                <label><input type="checkbox" checked={chkWeekly} onChange={e => setChkWeekly(e.target.checked)} /> Weekly plan</label>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.formLabel}>Status:</td>
                            <td style={styles.formInput}>
                                <label><input type="radio" checked={status === 'A'} onChange={() => setStatus('A')} /> Active</label>&nbsp;&nbsp;
                                <label><input type="radio" checked={status === ''} onChange={() => setStatus('')} /> Inactive</label>
                            </td>
                            <td style={styles.formLabel}>Remarks:</td>
                            <td style={styles.formInput}>
                                <input type="text" style={{ ...styles.txt, width: '90%' }} value={txtRemarks} onChange={e => setTxtRemarks(e.target.value)} />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} style={styles.formLabel}>When student does MathsDoctor</td>
                            <td style={styles.formLabel}>Send reports to eMail:</td>
                            <td style={styles.formInput}>
                                <input type="text" style={{ ...styles.txt, width: '90%' }} value={txtEmail} onChange={e => setTxtEmail(e.target.value)} />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={4} style={{ paddingTop: '15px', borderTop: '1px solid #ccc' }}>
                                <button style={styles.btn} onClick={() => window.history.back()}>Back</button>
                                <button
                                    style={saving ? styles.btnDisabled : styles.btn}
                                    disabled={saving}
                                    onClick={handleSave}
                                >
                                    {saving ? <Loading /> : "Save"}
                                </button>

                                {/* Usage Details - Enabled if row selected */}
                                <button
                                    style={selectedIdx !== -1 ? styles.btn : styles.btnDisabled}
                                    onClick={handleUsage}
                                    disabled={selectedIdx === -1}
                                >
                                    Usage Details
                                </button>

                                <button
                                    style={selectedIdx !== -1 ? styles.btn : styles.btnDisabled}
                                    onClick={handleDistinct}
                                    disabled={selectedIdx === -1}
                                >
                                    Distinct codes
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MD_ManageVoucherCodes;